export default `uniform float uTime;
uniform float uAtmosphere;

uniform vec3 uSunDir;
uniform vec3 uFlatSunDir;
uniform float uNight;
uniform float uSunset;
uniform float uDaylight;
uniform float uAfterglow;
uniform float uStarVisibility;

uniform vec3 uZenith;
uniform vec3 uHorizon;
uniform vec3 uGround;
uniform vec3 uDayGlow;
uniform vec3 uAfterglowColor;
uniform vec3 uSetBlue;
uniform vec3 uNightHorizon;

uniform vec3 uMilkyWarm;
uniform vec3 uMilkyCool;
uniform vec3 uMilkyDust;
uniform vec3 uStarWhite;
uniform vec3 uStarOrange;
uniform vec3 uStarBlue;

uniform mat3 uCelestialMatrix;
uniform mat3 uGalacticMatrix;
uniform samplerCube uMilkyMap;

uniform float uStorm;
uniform vec3 uStormColor;

varying vec3 vWorldPosition;

float hash13(vec3 p) {
    p = fract(p * vec3(0.1031, 0.1030, 0.0973));
    p += dot(p, p.yxz + 33.33);
    return fract((p.x + p.y) * p.z);
}

vec3 starLayer(vec3 dir, float cells, float baseThreshold, float milky) {
    vec3 a = abs(dir);
    vec2 uv;
    float face;

    if (a.x >= a.y && a.x >= a.z) {
        uv = dir.yz / dir.x;
        face = dir.x > 0.0 ? 0.0 : 1.0;
    } else if (a.y >= a.z) {
        uv = dir.xz / dir.y;
        face = dir.y > 0.0 ? 2.0 : 3.0;
    } else {
        uv = dir.xy / dir.z;
        face = dir.z > 0.0 ? 4.0 : 5.0;
    }

    uv = (uv * 0.5 + 0.5) * cells;
    vec2 id = floor(uv);
    vec2 gv = fract(uv) - 0.5;

    float n = hash13(vec3(id, face + 1.7));
    float threshold = mix(baseThreshold, baseThreshold - 0.05, clamp(milky, 0.0, 1.0));
    if (n < threshold) return vec3(0.0);

    vec2 jitter = (vec2(
        hash13(vec3(id, face + 11.0)),
        hash13(vec3(id, face + 23.0))
    ) - 0.5) * 0.6;

    float dist = length(gv - jitter);
    float mag = hash13(vec3(id, face + 37.0));
    float brightness = mix(0.25, 1.0, pow(mag, 2.6));
    float radius = mix(0.03, 0.055, brightness);
    float core = 1.0 - smoothstep(radius * 0.25, radius, dist);

    float phase = hash13(vec3(id, face + 5.0)) * 6.28318530718;
    float speed = mix(0.6, 2.2, hash13(vec3(id, face + 8.0)));
    float wave = 0.5 + 0.5 * sin(uTime * speed + phase);
    float doesBlink = step(0.62, hash13(vec3(id, face + 13.0)));
    float twinkle = mix(1.0, mix(0.78, 1.0, wave), doesBlink);

    float tint = hash13(vec3(id, face + 19.0));
    vec3 starColor = uStarWhite;
    starColor = mix(starColor, uStarOrange, smoothstep(0.86, 0.96, tint));
    starColor = mix(starColor, uStarBlue, smoothstep(0.975, 1.0, tint));

    float gain = mix(0.4, 1.2, brightness);
    return starColor * core * twinkle * gain;
}

void main() {
    vec3 dir = normalize(vWorldPosition - cameraPosition);

    
    
    float aboveGround = smoothstep(-0.42, -0.04, dir.y);
    float zenithMask = smoothstep(-0.12, 0.78, dir.y);
    zenithMask = pow(clamp(zenithMask, 0.0, 1.0), 1.15);

    vec3 color = mix(uGround, uHorizon, aboveGround);
    color = mix(color, uZenith, zenithMask);

    float dayWeight = uDaylight * (1.0 - uSunset);
    if (dayWeight > 0.0 || uAfterglow > 0.0 || uSunset > 0.0) {
        float sunFacing = clamp(dot(dir, uSunDir), -1.0, 1.0);

        if (dayWeight > 0.0) {
            float warm = pow(clamp(sunFacing * 0.5 + 0.5, 0.0, 1.0), 1.45);
            color = mix(color, uDayGlow, warm * dayWeight * 0.4);
        }

        if (uAfterglow > 0.0) {
            vec3 flatDir = vec3(dir.x, 0.0, dir.z);
            vec3 flatDirN = flatDir * inversesqrt(max(dot(flatDir, flatDir), 1e-6));
            float azimuth = clamp(dot(flatDirN, uFlatSunDir), 0.0, 1.0);
            float horizonBand = exp(-dir.y * dir.y * 18.0);
            color = mix(color, uAfterglowColor, pow(azimuth, 1.35) * horizonBand * uAfterglow * 0.85);
        }

        if (uSunset > 0.0) {
            
            float blue = pow(clamp(sunFacing, 0.0, 1.0), 2.4);
            float lowSky = exp(-max(dir.y, 0.0) * 1.7);
            color = mix(color, uSetBlue, blue * lowSky * uSunset * 0.82);
        }

        if (dayWeight > 0.0) {
            float opposition = smoothstep(0.2, -0.8, sunFacing);
            color = mix(color, color * vec3(0.74, 0.64, 0.60), opposition * dayWeight * 0.55);
        }
    }

    if (uNight * uAtmosphere > 0.0) {
        
        float sandy = pow(1.0 - smoothstep(-0.06, 0.62, dir.y), 1.35);
        color = mix(color, uNightHorizon, sandy * uNight * 0.85 * uAtmosphere);
    }

    
    if (uStarVisibility > 0.0 && dir.y > -0.12 && uStorm < 1.0) {
        vec3 celestial = uCelestialMatrix * dir;
        vec3 gal = uGalacticMatrix * dir;

        float band = exp(-gal.y * gal.y * 8.0);
        float core = exp(-gal.y * gal.y * 26.0);
        vec2 noise = texture(uMilkyMap, gal).rg;
        float clouds = noise.r;
        float dust = noise.g;
        float lanes = smoothstep(0.30, 0.68, dust);
        float milkyMask = band * smoothstep(0.25, 0.82, clouds);
        milkyMask *= mix(0.08, 1.0, lanes);
        milkyMask = clamp(milkyMask + core * clouds * 0.45, 0.0, 1.0);

        vec3 milkyColor = mix(uMilkyWarm, uMilkyCool, smoothstep(0.32, 0.78, clouds));
        milkyColor = mix(
            milkyColor,
            uMilkyDust,
            smoothstep(0.62, 0.92, dust) * band * 0.35
        );

        float skyLift = smoothstep(-0.12, 0.2, dir.y);
        float dustyExtinction = mix(0.42, 1.0, smoothstep(0.0, 0.32, dir.y));
        float horizonExtinction = mix(1.0, dustyExtinction, uAtmosphere);
        float starGain = uStarVisibility * skyLift * horizonExtinction;
        color += milkyColor * milkyMask * starGain * 0.5;

        vec3 stars = starLayer(celestial, 36.0, 0.86, milkyMask);
        stars += starLayer(celestial, 62.0, 0.93, milkyMask) * 0.55;
        color += stars * starGain;
    }

    
    color = mix(color, uStormColor, uStorm);

    float dither = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
    color += (dither - 0.5) * 0.0015;

    gl_FragColor = linearToOutputTexel(vec4(max(color, vec3(0.0)), 1.0));
}`